import React, { useState, useEffect } from 'react';
import { MarketProvider, useMarket } from './context/MarketContext';
import TradesPlatform from './TradesPlatform';
import WorkerApplication from './components/WorkerApplication';
import BookingConfirmed from './BookingConfirmed';
import { getUser, onAuthStateChange, signIn, signOut } from './lib/supabase';
import COLORS from './config/colors';

const WORKER_SIGNUP_PATHS = ['/work-with-us', '/workers', '/careers'];

function isWorkerSignupPath(pathname = window.location.pathname) {
  const path = (pathname || '/').replace(/\/+$/, '') || '/';
  return WORKER_SIGNUP_PATHS.includes(path);
}

function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const { user, error: authError } = await signIn(email, password);
    if (authError) {
      setError(authError);
      setLoading(false);
    } else {
      onLogin(user);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: COLORS.cream, fontFamily: "'DM Sans', sans-serif",
    }}>
      <div style={{
        background: COLORS.white, borderRadius: 20, padding: 40, width: 380,
        boxShadow: '0 4px 24px rgba(26,35,50,0.1)', border: '1px solid rgba(155,142,130,0.12)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 24, fontWeight: 700, fontFamily: "'Playfair Display', serif", color: COLORS.bark, letterSpacing: 1 }}>
            UltraLife Trades
          </div>
          <div style={{ fontSize: 13, color: COLORS.stone, marginTop: 4 }}>Admin Login</div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1.5, color: COLORS.stone, display: 'block', marginBottom: 6 }}>Email</label>
            <input
              type="email" value={email} onChange={e => setEmail(e.target.value)} required
              placeholder="admin@ultralifetrades.com"
              style={{
                width: '100%', padding: '12px 16px', borderRadius: 10,
                border: '1px solid rgba(155,142,130,0.3)', fontSize: 14,
                fontFamily: "'DM Sans', sans-serif", outline: 'none',
                background: COLORS.cream, boxSizing: 'border-box',
              }}
            />
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1.5, color: COLORS.stone, display: 'block', marginBottom: 6 }}>Password</label>
            <input
              type="password" value={password} onChange={e => setPassword(e.target.value)} required
              style={{
                width: '100%', padding: '12px 16px', borderRadius: 10,
                border: '1px solid rgba(155,142,130,0.3)', fontSize: 14,
                fontFamily: "'DM Sans', sans-serif", outline: 'none',
                background: COLORS.cream, boxSizing: 'border-box',
              }}
            />
          </div>

          {error && (
            <div style={{ color: COLORS.red, fontSize: 13, marginBottom: 16, textAlign: 'center' }}>{error}</div>
          )}

          <button
            type="submit" disabled={loading}
            style={{
              width: '100%', padding: '14px', borderRadius: 10, border: 'none',
              background: loading ? COLORS.stone : COLORS.bark, color: COLORS.honey,
              fontSize: 14, fontWeight: 600, cursor: loading ? 'wait' : 'pointer',
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

/** Loading screen shown while market/geo resolves */
function MarketLoadingScreen() {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: COLORS.cream, fontFamily: "'DM Sans', sans-serif", gap: 16,
    }}>
      <div style={{
        width: 36, height: 36, border: `3px solid ${COLORS.cream}`,
        borderTopColor: COLORS.honey, borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }} />
      <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "'Playfair Display', serif", color: COLORS.bark }}>
        UltraLife Trades
      </div>
      <div style={{ fontSize: 14, color: COLORS.stone }}>Finding your local market...</div>
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
}


// Admin email allowlist — set REACT_APP_ADMIN_EMAILS as comma-separated list.
// If the env var is empty/unset, NO logged-in user is granted admin access.
const ADMIN_EMAILS = (process.env.REACT_APP_ADMIN_EMAILS || '')
  .split(',')
  .map(e => e.trim().toLowerCase())
  .filter(Boolean);

function AppInner() {
  const { market, trade, geoLoading, isReady } = useMarket();
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    getUser().then(u => setUser(u || null));
    const subscription = onAuthStateChange(u => setUser(u || null));
    return () => subscription?.unsubscribe?.();
  }, []);

  // Dynamic page title based on market + trade
  useEffect(() => {
    if (market && trade) {
      document.title = `${trade.displayName} in ${market.displayName}, ${market.state} | UltraLife Trades`;
    } else {
      document.title = "UltraLife Trades | Premium Home Improvement";
    }
  }, [market, trade]);

  // Auth loading
  if (user === undefined) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: COLORS.cream }}>
        <div style={{ color: COLORS.stone, fontFamily: "'DM Sans', sans-serif" }}>Loading...</div>
      </div>
    );
  }

  // Dedicated routes that bypass market resolution
  if (isWorkerSignupPath()) {
    return <WorkerApplication />;
  }

  // Market resolving via geolocation
  if (geoLoading || !isReady) {
    return <MarketLoadingScreen />;
  }

  const isAdmin = !!user && ADMIN_EMAILS.length > 0
    && ADMIN_EMAILS.includes((user.email || '').toLowerCase());

  return (
    <TradesPlatform
      market={market}
      trade={trade}
      isAdmin={isAdmin}
      user={user}
      onLogout={async () => { await signOut(); setUser(null); }}
      LoginScreen={LoginScreen}
    />
  );
}

export default function App() {
  // Handle /booking-confirmed route before loading market context
  if (window.location.pathname === '/booking-confirmed') {
    return <BookingConfirmed />;
  }

  // Worker signup: bypass MarketProvider so /workers and /careers never flash the quote loader
  if (isWorkerSignupPath()) {
    return <WorkerApplication />;
  }

  return (
    <MarketProvider>
      <AppInner />
    </MarketProvider>
  );
}
